import logger from './logger';
/**
 * Safe storage wrapper for localStorage and sessionStorage
 * Handles Safari Private Mode, quota exceeded errors, and provides in-memory fallback
 */

class SafeStorage {
    private inMemoryStorage = new Map<string, string>();
    private useInMemory: boolean = false;
    private storageType: 'local' | 'session';

    constructor(storageType: 'local' | 'session') {
        this.storageType = storageType;
        this.testStorage();
    }

    private testStorage(): void {
        const storage = this.storageType === 'local' ? localStorage : sessionStorage;
        try {
            const testKey = '__storage_test__';
            storage.setItem(testKey, 'test');
            storage.removeItem(testKey);
            this.useInMemory = false;
        } catch (e) {
            logger.warn(`${this.storageType}Storage not available, using in-memory fallback`, e);
            this.useInMemory = true;
        }
    }

    getItem(key: string): string | null {
        if (this.useInMemory) {
            return this.inMemoryStorage.get(key) || null;
        }

        try {
            const storage = this.storageType === 'local' ? localStorage : sessionStorage;
            return storage.getItem(key);
        } catch (e) {
            logger.warn(`Failed to get item from ${this.storageType}Storage:`, key, e);
            return this.inMemoryStorage.get(key) || null;
        }
    }

    setItem(key: string, value: string): void {
        if (this.useInMemory) {
            this.inMemoryStorage.set(key, value);
            return;
        }

        try {
            const storage = this.storageType === 'local' ? localStorage : sessionStorage;
            storage.setItem(key, value);
            // Also keep in memory as backup
            this.inMemoryStorage.set(key, value);
        } catch (e) {
            if (e instanceof DOMException && (
                e.name === 'QuotaExceededError' ||
                e.name === 'NS_ERROR_DOM_QUOTA_REACHED'
            )) {
                logger.warn(`${this.storageType}Storage quota exceeded, using in-memory fallback`);
                this.useInMemory = true;
                this.inMemoryStorage.set(key, value);
            } else {
                logger.error(`Failed to set item in ${this.storageType}Storage:`, key, e);
                this.inMemoryStorage.set(key, value);
            }
        }
    }

    removeItem(key: string): void {
        if (this.useInMemory) {
            this.inMemoryStorage.delete(key);
            return;
        }

        try {
            const storage = this.storageType === 'local' ? localStorage : sessionStorage;
            storage.removeItem(key);
            this.inMemoryStorage.delete(key);
        } catch (e) {
            logger.warn(`Failed to remove item from ${this.storageType}Storage:`, key, e);
            this.inMemoryStorage.delete(key);
        }
    }

    clear(): void {
        if (this.useInMemory) {
            this.inMemoryStorage.clear();
            return;
        }

        try {
            const storage = this.storageType === 'local' ? localStorage : sessionStorage;
            storage.clear();
            this.inMemoryStorage.clear();
        } catch (e) {
            logger.warn(`Failed to clear ${this.storageType}Storage:`, e);
            this.inMemoryStorage.clear();
        }
    }

    key(index: number): string | null {
        if (this.useInMemory) {
            const keys = Array.from(this.inMemoryStorage.keys());
            return keys[index] || null;
        }

        try {
            const storage = this.storageType === 'local' ? localStorage : sessionStorage;
            return storage.key(index);
        } catch (e) {
            logger.warn(`Failed to get key from ${this.storageType}Storage:`, index, e);
            const keys = Array.from(this.inMemoryStorage.keys());
            return keys[index] || null;
        }
    }

    get length(): number {
        if (this.useInMemory) {
            return this.inMemoryStorage.size;
        }

        try {
            const storage = this.storageType === 'local' ? localStorage : sessionStorage;
            return storage.length;
        } catch (e) {
            logger.warn(`Failed to get length from ${this.storageType}Storage:`, e);
            return this.inMemoryStorage.size;
        }
    }
}

// Export singleton instances
export const safeLocalStorage = new SafeStorage('local');
export const safeSessionStorage = new SafeStorage('session');

// Helper functions for common patterns
export const getStorageItem = (key: string, isSession = false): string | null => {
    return isSession ? safeSessionStorage.getItem(key) : safeLocalStorage.getItem(key);
};

export const setStorageItem = (key: string, value: string, isSession = false): void => {
    if (isSession) {
        safeSessionStorage.setItem(key, value);
    } else {
        safeLocalStorage.setItem(key, value);
    }
};

export const removeStorageItem = (key: string, isSession = false): void => {
    if (isSession) {
        safeSessionStorage.removeItem(key);
    } else {
        safeLocalStorage.removeItem(key);
    }
};

// JSON helpers
export const getStorageJSON = <T>(key: string, isSession = false): T | null => {
    const item = getStorageItem(key, isSession);
    if (!item) {return null;}
    
    try {
        return JSON.parse(item) as T;
    } catch (e) {
        logger.warn('Failed to parse JSON from storage:', key, e);
        return null;
    }
};

export const setStorageJSON = <T>(key: string, value: T, isSession = false): void => {
    try {
        const json = JSON.stringify(value);
        setStorageItem(key, json, isSession);
    } catch (e) {
        logger.error('Failed to stringify JSON for storage:', key, e);
    }
};

export const getStorageNumber = (key: string, defaultValue: number, isSession = false): number => {
    const item = getStorageItem(key, isSession);
    if (!item) return defaultValue;
    
    const parsed = Number(item);
    return isNaN(parsed) ? defaultValue : parsed;
};

export const getStorageBoolean = (key: string, defaultValue: boolean, isSession = false): boolean => {
    const item = getStorageItem(key, isSession);
    if (item === null) return defaultValue;
    return item === 'true';
};

